import { Fragment } from 'react';
import { ArrowLeft } from 'lucide-react';
import { uniqueId } from 'lodash';
import { toast } from 'sonner';
import { useMutation } from '@apollo/client/react';

import { AssetCreateCustomDocument } from 'src/graphql/actions/integration/assetCreateCustom.mutation';
import { useAddDeposit } from 'src/context/deposit';
import { ClientError, getApiError } from 'src/utils/errors';
import { DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Table, TableHeader, TableRow, TableHead, TableBody, TableCell } from '@/components/ui/table';

const CustomDepositConfirmContainer = () => {
  const [createCustomAssets, { loading }] = useMutation(AssetCreateCustomDocument);
  const {
    ui: { integration, selectedEscrowId, customData: assetsInput, setInitialData, getBack },
    contentType,
    refetch,
  } = useAddDeposit();

  const onConfirm = async () => {
    try {
      const { data } = await createCustomAssets({
        variables: {
          softwareEscrowId: selectedEscrowId,
          integrationId: integration?.id || '',
          assetsParams: assetsInput.map((asset) => ({
            ...asset,
            contentType: contentType!,
          })),
        },
      });

      if (data?.assetCreateCustom?.success) {
        toast.success('Assets added!', {
          description: 'Your assets have been added to this escrow.',
        });
        setInitialData();
        refetch?.();
      } else {
        throw new ClientError(data?.assetCreateCustom?.errors?.[0] ?? 'An error occurred');
      }
    } catch (e) {
      getApiError(e, toast.error);
    }
  };

  return (
    <Fragment>
      <DialogHeader className="p-6 pb-0">
        <DialogTitle className="text-xl font-bold">
          Are you sure you want to add these assets to your escrow?
        </DialogTitle>
        <DialogDescription>
          These assets will be deposited into our secure vaults, and will be included in the escrow material.
        </DialogDescription>
      </DialogHeader>
      <div className="overflow-y-auto px-6">
        <div className="border border-border rounded-md">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="font-bold">Name</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {assetsInput.map(({ title, url }) => (
                <TableRow key={uniqueId('asset-')}>
                  <TableCell>{title || url}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </div>
      <div className="flex justify-between gap-2 p-6 pt-0">
        <Button variant="outline" onClick={getBack}>
          <ArrowLeft />
          Back
        </Button>
        <div className="flex justify-end gap-2 grow">
          <Button variant="secondary" onClick={setInitialData}>
            Cancel
          </Button>
          <Button onClick={onConfirm} isLoading={loading}>
            Add
          </Button>
        </div>
      </div>
    </Fragment>
  );
};

export default CustomDepositConfirmContainer;
